import React, { useMemo, useState } from 'react';
import useIntl from 'react-intl/src/components/useIntl';
import { Modal, Form, Button, Input, Select } from 'semantic-ui-react';
import {
  useCreateCustomField,
  useUpdateCustomField,
} from 'src/hooks/useCustomFields';
import { errorToast } from 'src/api';
import messages from 'src/messages';
import { KIND_SETTINGS } from './customFields/kindSettings';
import { ValueResetWarning } from './customFields/ValueResetWarning';
import {
  buildFieldPayload,
  FIELD_TYPES,
  validateNumberFieldDefinition,
  type CustomFieldKind,
  type FieldDraft,
} from './customFields/fieldDefinitions';

/** Seed the draft from the field being edited, or the defaults for a new one. */
function initialDraft(existingField: any | null): FieldDraft {
  const kind: CustomFieldKind = existingField?.kind ?? 'text';
  const hasTextDefault = kind !== 'boolean' && kind !== 'list';

  return {
    name: existingField?.name ?? '',
    kind,
    defaultText:
      existingField && hasTextDefault
        ? String(existingField.default ?? '')
        : '',
    boolDefault: kind === 'boolean' ? !!existingField.default : true,
    precision: existingField?.precision ?? 0,
    minVal: existingField?.min != null ? String(existingField.min) : '',
    maxVal: existingField?.max != null ? String(existingField.max) : '',
    choices: existingField?.choices ?? [],
    listDefault: kind === 'list' ? (existingField?.default ?? []) : [],
  };
}

/** Pull a readable message out of whatever shape the API rejected us with. */
function saveErrorDetail(e: any): string | null {
  const detail = e?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return (
      detail
        .map((d) => d?.msg)
        .filter(Boolean)
        .join('; ') || null
    );
  }
  return e?.message || null;
}

export function CustomFieldModal({
  projectId,
  category,
  existingField,
  onClose,
}: {
  projectId: string;
  category: string;
  existingField: any | null;
  onClose: () => void;
}) {
  const { formatMessage } = useIntl();
  const createMutation = useCreateCustomField(projectId);
  const updateMutation = useUpdateCustomField(projectId);

  const [draft, setDraft] = useState<FieldDraft>(() =>
    initialDraft(existingField)
  );
  const [saving, setSaving] = useState(false);

  const update = (patch: Partial<FieldDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const { name, kind, precision, minVal, maxVal, defaultText } = draft;

  const numberErrors = useMemo(
    () =>
      kind === 'number'
        ? validateNumberFieldDefinition({
            precision,
            minVal,
            maxVal,
            defaultText,
          })
        : [],
    [kind, precision, minVal, maxVal, defaultText]
  );

  const typeOptions = useMemo(
    () =>
      FIELD_TYPES.map(({ kind: value, label }) => ({
        key: value,
        value,
        text: formatMessage(label),
      })),
    [formatMessage]
  );

  const handleSave = async () => {
    if (numberErrors.length > 0) return;

    setSaving(true);
    try {
      const field = buildFieldPayload(draft);
      if (existingField) {
        await updateMutation.mutateAsync({
          category,
          fieldId: existingField.id,
          field,
        });
      } else {
        await createMutation.mutateAsync({ category, field });
      }
      onClose();
    } catch (e: any) {
      errorToast(
        saveErrorDetail(e) || formatMessage(messages.customFieldSaveFailed)
      );
    } finally {
      setSaving(false);
    }
  };

  const KindSettings = KIND_SETTINGS[kind];
  const title = formatMessage(
    existingField ? messages.customFieldEditTitle : messages.customFieldNewTitle
  );
  const canSave = !!name.trim() && !saving && numberErrors.length === 0;

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header>
        {title}{' '}
        <Button circular icon="close" onClick={onClose} floated="right" />
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field required>
            <label>{formatMessage(messages.name)}</label>
            <Input
              value={name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder={formatMessage(messages.customFieldNamePlaceholder)}
            />
          </Form.Field>

          <Form.Field>
            <label>{formatMessage(messages.customFieldType)}</label>
            <Select
              options={typeOptions}
              value={kind}
              onChange={(_, d) => update({ kind: d.value as CustomFieldKind })}
            />
          </Form.Field>

          <ValueResetWarning existingField={existingField} draft={draft} />

          <KindSettings draft={draft} errors={numberErrors} onChange={update} />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{formatMessage(messages.cancel)}</Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          loading={saving}
          color="blue"
        >
          {formatMessage(messages.ok)}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
